/**
 * Shared configuration constants — values that both the mobile client and the
 * Cloud Functions backend need to agree on.
 *
 * Anything that changes per-environment (dev vs prod) should still live in an
 * env file; this module is for **client + server contract** values only.
 */

/**
 * Base URL used to build shareable invite links: `${INVITE_LINK_BASE_URL}/invite/{token}`.
 *
 * Points at the production custom domain. `web.travelix.app` is CDN-fronted
 * by Cloudflare in front of Firebase Hosting; the app claims both
 * `web.travelix.app` and `tripifyx.web.app` as Android App Links hosts, so
 * previously-shared `tripifyx.web.app` links keep working too.
 *
 * The client uses this to compose the URL for "Copy" / "Share". The server
 * uses it only when we later add the "Send by email" companion action.
 */
export const INVITE_LINK_BASE_URL = "https://web.travelix.app";

/**
 * Path segment appended to INVITE_LINK_BASE_URL to build a full invite URL.
 * The mobile app's router registers this same path at `/invite/:token`.
 */
export const INVITE_LINK_PATH = "/invite";

/** Compose a shareable invite URL for the given token. */
export function inviteUrlFor(token: string): string {
  return `${INVITE_LINK_BASE_URL}${INVITE_LINK_PATH}/${token}`;
}

// ─── Invite link defaults ────────────────────────────────────────────────────
// Applied by the "Share invite link" UI on first render. Each field remains
// editable (including "no cap" / "no expiration") — these are just seeds.

/** Default seat cap when the owner opens the create-link form. */
export const INVITE_LINK_DEFAULT_MAX_ACCEPTS = 5;

/** Default expiration window in days. */
export const INVITE_LINK_DEFAULT_EXPIRATION_DAYS = 7;

/** Hard upper bound for seat caps — protects against a runaway typo. */
export const INVITE_LINK_MAX_ACCEPTS_LIMIT = 100;

/** Hard upper bound for expiration in days — 1 year. */
export const INVITE_LINK_MAX_EXPIRATION_DAYS = 365;
